import { VCS_SLUG } from '../../constants/api';
import { clearBranchName, replaceAsciiCharWithSlash, replaceSlashWithAsciiChar } from '../../utils/resolveBranch';
import BaseEntity from './BaseEntity';
import Branch from './Branch';
import RepositoryToken from './RepositoryToken';
export default class Repository extends BaseEntity {
    constructor(repository, commits, defects, branchName, codeCoverage) {
        super();
        this.branches = [];
        this.avatar = null;
        this.lastBranch = null;
        this.lastDatetime = null;
        this.lastCommitUpdatedAt = null;
        this.hookUid = null;
        this.codeCoverage = null;
        this.vcsAnnotationsEnabled = null;
        this.active = false;
        this.webhookLoading = false;
        this.disabled = false;
        this.tokens = [];
        this.size = 0; // TODO: missing in graphql response
        this.id = this.parseProperty(repository, 'id');
        this.name = this.parseProperty(repository, 'name');
        this.fullname = this.parseProperty(repository, 'fullname');
        this.slug = this.parseProperty(repository, 'slug');
        this.vcs = this.parseProperty(repository, 'vcs');
        if ('active' in repository) {
            this.active = this.parseProperty(repository, 'active');
        }
        this.private = this.parseProperty(repository, 'private');
        this.admins = this.parseProperty(repository, 'admins');
        this.uuid = this.parseProperty(repository, 'uuid');
        this.avatar = this.parseProperty(repository, 'avatar');
        this.lastBranch = this.parseProperty(repository, 'lastBranch');
        this.lastDatetime = this.parseProperty(repository, 'lastDatetime');
        this.lastCommitUpdatedAt = this.parseProperty(repository, 'lastCommitUpdatedAt');
        this.hookUid = this.parseProperty(repository, 'hookUid');
        this.codeCoverage = codeCoverage ?? null;
        this.vcsAnnotationsEnabled = this.parseProperty(repository, 'vcsAnnotationsEnabled');
        if ('size' in repository) {
            this.size = this.parseProperty(repository, 'size');
        }
        if (this.hasBranches(repository)) {
            const listOfCommits = {
                ...commits,
                currentPage: 1,
            };
            const branches = this.parseProperty(repository, 'branches');
            this.branches = branches.map((branch) => new Branch(branch, listOfCommits, defects, codeCoverage));
        }
        if ('tokens' in repository && repository.tokens) {
            this.tokens = this.parseProperty(repository, 'tokens').map(token => new RepositoryToken(token));
        }
        if ('webhookLoading' in repository) {
            this.webhookLoading = this.parseProperty(repository, 'webhookLoading');
        }
        if ('disabled' in repository) {
            this.disabled = this.parseProperty(repository, 'disabled');
        }
        this.branchName = branchName;
    }
    getMainBranch() {
        if (!this.branches.length) {
            throw new Error('Repository has no branches loaded.');
        }
        const mainBranch = this.branches.find(({ main }) => main);
        if (!mainBranch) {
            return this.getLastBranch();
        }
        return mainBranch;
    }
    getBranchIds() {
        return this.branches.map((branch) => branch.id);
    }
    getBranchByName(branchName) {
        const branchNameFormatted = clearBranchName(replaceAsciiCharWithSlash(branchName));
        const branch = this.branches.find(({ name }) => branchNameFormatted === name);
        if (!branch) {
            const availableBranches = this.branches.map(({ name }) => name);
            throw new Error(`Branch ${branchName} doesn't exist. Try one of [${availableBranches.join(', ')}]`);
        }
        return branch;
    }
    getBranchById(branchId) {
        const branch = this.branches.find(({ id }) => id === branchId);
        if (!branch) {
            throw new Error('Branch doesn\'t exist.');
        }
        return branch;
    }
    getLastBranch() {
        if (this.lastBranch) {
            return this.getBranchByName(this.lastBranch);
        }
        return this.branches[0];
    }
    hasBranches(repository) {
        return Object.prototype.hasOwnProperty.call(repository, 'branches') && repository.branches.length;
    }
    setWebhookLoading(isLoading) {
        this.webhookLoading = isLoading;
    }
    setDisabled(isDisabled) {
        this.disabled = isDisabled;
    }
    setActive(isActive) {
        this.active = isActive;
    }
    getOwner() {
        const repositoryNameIndexBy = this.slug ?? '/';
        const repositoryOwnerRaw = this.fullname.substring(0, this.fullname.lastIndexOf(repositoryNameIndexBy));
        const repositoryOwnerRawLength = repositoryOwnerRaw.length - 1;
        const replaceLastSlash = repositoryOwnerRaw.lastIndexOf('/') === repositoryOwnerRawLength;
        return replaceLastSlash ? repositoryOwnerRaw.substring(0, repositoryOwnerRawLength) : repositoryOwnerRaw;
    }
    getName() {
        return this.fullname.substring(this.slug ? this.fullname.lastIndexOf(this.slug) : this.fullname.lastIndexOf('/') + 1);
    }
    getFullName() {
        const cleanRepositoryName = this.slug ? this.slug.replace(/[\s_]/g, '-') : this.name.toLowerCase().replace(/[\s_]/g, '-');
        const cleanRepositoryFullname = this.fullname.toLowerCase().replace(/_/g, '-');
        const index = cleanRepositoryFullname.lastIndexOf(cleanRepositoryName);
        if (index === -1) {
            return this.fullname;
        }
        const owner = replaceSlashWithAsciiChar(this.fullname.substring(0, index - 1));
        const repositoryName = replaceSlashWithAsciiChar(this.fullname.substring(index));
        return `${owner}/${repositoryName}`;
    }
    getTitleParts() {
        return {
            titlePrimary: this.getName(),
            titleSecondary: this.getOwner(),
        };
    }
    getBaseLink() {
        return `/${VCS_SLUG[this.vcs]}/${this.getFullName()}`;
    }
    setTokens(tokens) {
        this.tokens = tokens;
    }
}
